/**
 * Generic hand-rolled SVG donut for a small, ordered set of slices —
 * used by the platform-only Starlink health / Peplink connectivity
 * tiles on the Dashboard. Geometry mirrors `StateDonut` / `TagHealthDonut`
 * (straight `<path d="A ...">` arcs, one segment per slice with value > 0,
 * a full-ring short-circuit for the single-slice case, and a total label
 * in the center hole) so all four dashboard donuts line up visually.
 *
 * Unlike the state/tag donuts this one is data-agnostic: the caller passes
 * ordered `{label, value, color}` slices, so the same component renders
 * both connectivity tiles. Zero-value slices are omitted from the ring
 * (like StateDonut) but still listed in the sibling legend as 0 — the
 * legend is the honest record when a whole class is empty (e.g. the
 * Starlink poller is down fleet-wide, so "online" reads 0).
 */
import React from "react";

export interface DonutSlice {
  label: string;
  value: number;
  /** Hex color for the ring segment + legend swatch. */
  color: string;
}

interface ConnectivityDonutProps {
  slices: DonutSlice[];
  /** Center-hole unit label under the total (e.g. "boxes"). */
  unitLabel?: string;
  size?: number;
  /** Stroke width as a fraction of radius (0..1). 0.45 ≈ classic donut. */
  thickness?: number;
}

const EMPTY_RING_HEX = "#e5e7eb"; // gray-200 — matches the sibling donuts' empty ring

function polar(cx: number, cy: number, r: number, angle: number): [number, number] {
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

function arcPath(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  start: number,
  end: number,
): string {
  const [x1, y1] = polar(cx, cy, rOuter, start);
  const [x2, y2] = polar(cx, cy, rOuter, end);
  const [x3, y3] = polar(cx, cy, rInner, end);
  const [x4, y4] = polar(cx, cy, rInner, start);
  const largeArc = end - start > Math.PI ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
}

const CenterLabel: React.FC<{
  cx: number;
  cy: number;
  size: number;
  total: number;
  unitLabel: string;
}> = ({ cx, cy, size, total, unitLabel }) => {
  if (total === 0) {
    return (
      <text
        x={cx}
        y={cy + 6}
        textAnchor="middle"
        className="fill-secondary-400 text-base"
      >
        —
      </text>
    );
  }
  return (
    <>
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        className="fill-secondary-900 font-semibold"
        style={{ fontSize: size * 0.18 }}
      >
        {total.toLocaleString()}
      </text>
      <text
        x={cx}
        y={cy + size * 0.12}
        textAnchor="middle"
        className="fill-secondary-500"
        style={{ fontSize: size * 0.06 }}
      >
        {unitLabel}
      </text>
    </>
  );
};

export const ConnectivityDonut: React.FC<ConnectivityDonutProps> = ({
  slices,
  unitLabel = "boxes",
  size = 220,
  thickness = 0.45,
}) => {
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 4; // leave 4px padding for stroke aesthetics
  const rInner = rOuter * (1 - thickness);
  const rMid = (rOuter + rInner) / 2;
  const strokeWidth = rOuter - rInner;
  const label = (
    <CenterLabel cx={cx} cy={cy} size={size} total={total} unitLabel={unitLabel} />
  );

  // Empty state — nothing to show. Flat gray ring with "—".
  if (total === 0) {
    return (
      <svg width={size} height={size} role="img" aria-label="No data">
        <circle
          cx={cx}
          cy={cy}
          r={rMid}
          fill="none"
          stroke={EMPTY_RING_HEX}
          strokeWidth={strokeWidth}
        />
        {label}
      </svg>
    );
  }

  // Build segments. Start at the top (-π/2) and go clockwise, skipping
  // zero-value slices.
  let cursor = -Math.PI / 2;
  const segments = slices.flatMap((sl) => {
    if (sl.value === 0) return [];
    const sweep = (sl.value / total) * Math.PI * 2;
    const start = cursor;
    const end = cursor + sweep;
    cursor = end;
    return [{ ...sl, start, end }];
  });

  // Single-segment short-circuit: a full ring as a single circle reads
  // cleaner than a 360° path (start == end collapses to nothing).
  if (segments.length === 1) {
    const only = segments[0];
    return (
      <svg width={size} height={size} role="img" aria-label="Connectivity donut">
        <circle
          cx={cx}
          cy={cy}
          r={rMid}
          fill="none"
          stroke={only.color}
          strokeWidth={strokeWidth}
        />
        {label}
      </svg>
    );
  }

  return (
    <svg width={size} height={size} role="img" aria-label="Connectivity donut">
      {segments.map((seg) => (
        <path
          key={seg.label}
          d={arcPath(cx, cy, rOuter, rInner, seg.start, seg.end)}
          fill={seg.color}
        >
          <title>{`${seg.label}: ${seg.value.toLocaleString()}`}</title>
        </path>
      ))}
      {label}
    </svg>
  );
};

/** Compact sibling legend: one row per slice with a color swatch, label,
 *  count, and percentage. Zero-value slices stay listed (dimmed) so the
 *  legend is stable across snapshots and reads honestly when a class is
 *  empty. */
export const ConnectivityLegend: React.FC<{ slices: DonutSlice[] }> = ({
  slices,
}) => {
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  return (
    <ul className="space-y-1.5 text-sm">
      {slices.map((sl) => {
        const pct = total > 0 ? Math.round((sl.value / total) * 100) : 0;
        const dim = sl.value === 0;
        return (
          <li
            key={sl.label}
            className={"flex items-center gap-2 " + (dim ? "opacity-40" : "")}
          >
            <span
              className="inline-block w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor: sl.color }}
              aria-hidden
            />
            <span className="flex-1 text-secondary-700">{sl.label}</span>
            <span className="font-mono font-semibold text-secondary-900">
              {sl.value}
            </span>
            <span className="text-secondary-500 text-xs w-10 text-right">
              {pct}%
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default ConnectivityDonut;
